/**
 * StoreStatusContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import StoreStatusPresenter from './StoreStatusPresenter';
import {
  useGetShopInformations,
  useUpdateShopInformation,
} from 'hooks/ShopInformationHooks';
import { useUserStack } from 'store';
import MessageAlert from 'utils';
import { LoadingPage } from 'components';
import Formatter from 'utils/Formatter';
import { useFileUpload } from 'hooks/FileHooks';
import { useCreateShopInformationFile } from 'hooks/ShopInformationFileHooks';

const StoreStatusContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const userInfo = useUserStack();

  /* ===== Mutate ===== */
  const { updateShopInformation } = useUpdateShopInformation();
  const { createShopInformationFile } = useCreateShopInformationFile();
  const { fileUpload } = useFileUpload();

  /* ===== Data ===== */
  // 주문 전체 조회
  const join_tables =
    '%5B%22admin%22,%22shop_information_file%22,%22shop_menu%22,%22staff%22%5D';
  const { data: ShopInfo, refetch } = useGetShopInformations({
    shop_information_id: userInfo?.shop_information_id,
    join_tables: join_tables,
  });

  /* ===== STATE ===== */
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  // 모달
  const [isOpened, setIsOpened] = useState(false);
  // 모달 데이터
  const [modalData, setModalData] = useState({
    store: {},
  });
  // 모달 타입
  const [modalType, setModalType] = useState('read');

  // 리패칭
  const [isRefetch, setIsRefetch] = useState(false);

  const [thumbnailFiles, setThumbnailFiles] = React.useState([]);
  const [storeFiles, setStoreFiles] = React.useState([]);
  // 매장 위도/경도
  const [coords, setCoords] = useState({
    latitude: 0, // 위도
    longitude: 0, // 경도
  });

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**
   * 모달창 핸들러
   * --
   */
  const handleModalOpen = (data = {}) => {
    setModalData(data);
    setIsOpened(!isOpened);
  };

  /* ===== Function ===== */
  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o, callback = null) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  /**
   * 데이터 생성
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE') => {
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();
    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      STORE: '매장',
      CREATE: '생성',
      UPDATE: '수정',
    };

    console.log(t, s);
    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = handleValidateCheck(type);
    if (!data) return;

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      STORE: {
        UPDATE: updateShopInformation,
      },
    };

    try {
      // 로딩 처리
      setIsLoading(true);

      // 데이터 생성/수정
      const { status } = await submitFunc[type][submit]({
        shop_information_id: userInfo?.shop_information_id,
        ...data,
      });

      // 데이터 업데이트 성공 시
      if (status === 200) {
        if (
          thumbnailFiles?.length > 0 ||
          // menuFiles?.length > 0 ||
          storeFiles?.length > 0
          // licenseFiles?.length > 0 ||
          // etcFiles?.length > 0
        ) {
          // 파일 리스트 정제
          const files = [
            ...thumbnailFiles.map((file) => {
              file.category = 'THUMBNAIL';
              return file;
            }),
            // ...menuFiles.map((file) => {
            //   file.category = 'MENU';
            //   return file;
            // }),
            ...storeFiles.map((file) => {
              file.category = 'STORE';
              return file;
            }),
            // ...licenseFiles.map((file) => {
            //   file.category = 'LICENSE';
            //   return file;
            // }),
            // ...etcFiles.map((file) => {
            //   file.category = 'ETC';
            //   return file;
            // }),
          ];
          const shop_information_id = userInfo?.shop_information_id; // 생성된 매장 ID
          const fileSavePromises = files.map(async (file, index) => {
            const timestamp = Date.now();
            const fileExtension = file?.name?.split('.').pop();
            const baseName = file?.name?.replace(`.${fileExtension}`, '');
            const newBaseName = baseName?.replace(/[^a-zA-Z0-9_.-]/g, '_');
            const fileName = `jm_coffee/shop/${shop_information_id}/${newBaseName}_${timestamp}.${fileExtension}`;
            if (file.name) {
              // 각 파일을 개별적으로 업로드
              const uploadResponse = await fileUpload({
                file,
                file_name: fileName,
              });

              if (uploadResponse) {
                // 파일 정보 저장
                const shopFileInfo = {
                  shop_information_id,
                  file_name: `${newBaseName}_${timestamp}.${fileExtension}`,
                  file_url: fileName,
                  type: file.category,
                };
                return createShopInformationFile(shopFileInfo);
              }
            }
          });

          // 모든 파일 저장이 완료될 때까지 대기
          await Promise.all(fileSavePromises);
        }

        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );
        // 값 초기화
        setModalData((prev) => ({ ...prev, t: {} }));
        // 모달 닫기
        setIsOpened(!isOpened);
        // 리패치
        setIsRefetch(true);

        return;
      }

      MessageAlert.warning(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
      );
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'STORE') {
      // 지점 정보 유효성 검사
      const {
        shop_information_name,
        business_num,
        created_at,
        shop_information_address,
        // store_num,
      } = modalData?.store || {};

      // 매장명 입력값 체크
      if (!shop_information_name) {
        MessageAlert.warning('매장명을 입력해주세요.');
        return false;
      }

      // 사업자등록번호 입력값 체크
      if (!business_num) {
        MessageAlert.warning('사업자등록번호를 입력해주세요.');
        return false;
      }

      // 주소 입력값 체크
      if (!shop_information_address) {
        MessageAlert.warning('주소를 입력해주세요.');
        return false;
      }

      return {
        ...modalData?.store,
        ...coords,
        store_information: {
          business_hour: modalData?.store?.business_hour,
          business_num: modalData?.store?.business_num,
          store_num: modalData?.store?.store_num,
          services: {
            GROUP: modalData?.store?.GROUP,
            WIFI: modalData?.store?.WIFI,
            PARKING: modalData?.store?.PARKING,
            RESTROOM: modalData?.store?.RESTROOM,
            PACKAGING: modalData?.store?.PACKAGING,
            WHEELCHAIR: modalData?.store?.WHEELCHAIR,
          },
        },
        created_at: created_at ? Formatter.dateToUnix(created_at) : null,
      };
    }
  };

  /**
   * 좌표값 바인딩
   * --
   */
  const handleCoordChange = (lat, lng) => {
    setCoords({
      latitude: lat,
      longitude: lng,
    });
  };

  /* ===== HOOKS ===== */
  /**
   * 모달 타입 초기화
   * --
   */
  useEffect(() => {
    if (!isOpened) setModalType('read');
  }, [isOpened]);

  /**
   * 데이터 초기화
   * --
   */
  useEffect(() => {
    const newShopInfo = {
      ...ShopInfo?.data?.[0],
      admin: ShopInfo?.data?.[0]?.admin?.find(
        (admin) => admin?.admin_id === userInfo?.admin_id
      ),
    };

    setData(newShopInfo);
    // eslint-disable-next-line
  }, [ShopInfo]);

  useEffect(() => {
    if (isRefetch) {
      refetch().then(() => {
        setIsRefetch(false);
      });
    }
  }, [isRefetch, refetch]);

  // 파일데이터 정제
  useEffect(() => {
    const store = ShopInfo?.data;
    // console.log('store', store);

    // 초기 값을 설정하고 각 파일 데이터를 그룹화
    const groupedFiles = store?.reduce(
      (acc, order) => {
        order?.shop_information_file?.forEach((file) => {
          if (file.type === 'THUMBNAIL') {
            acc.thumbnail.push(file);
          } else if (file.type === 'STORE') {
            acc.store.push(file);
          } else if (file.type === 'LICENSE') {
            acc.license.push(file);
          } else if (file.type === 'MENU') {
            acc.menu.push(file);
          } else {
            acc.etc.push(file);
          }
        });
        return acc;
      },
      { menu: [], thumbnail: [], store: [], license: [], etc: [] } // 초기 값 설정
    );

    // 상태에 각각 저장
    setThumbnailFiles(groupedFiles?.thumbnail);
    setStoreFiles(groupedFiles?.store);
  }, [ShopInfo]);

  console.log(data);

  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <StoreStatusPresenter
        data={data}
        isOpened={isOpened}
        modalData={modalData}
        modalType={modalType}
        onModalChange={setModalType}
        onClose={handleModalOpen}
        onOpen={handleModal}
        isRefetch={isRefetch}
        onChange={handleChange}
        onSubmit={handleSubmit}
        userInfo={userInfo}
        files={{
          thumbnail: thumbnailFiles,
          store: storeFiles,
        }}
        onThumbnailFileChange={setThumbnailFiles}
        onStoreFileChange={setStoreFiles}
        onCoordChange={handleCoordChange}
      />
    </>
  );
};

export default StoreStatusContainer;
